import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import useSWR from 'swr';
import dayjs, { type Dayjs } from 'dayjs';
import { Box, styled } from '@mui/material';
import DatePicker from '@/components/@IntranetLibrary/DatePicker';
import Label from '@/components/@IntranetLibrary/Label';
import { RadioGroupV2 } from '@/components/@IntranetLibrary/Radio/RadioV2';
import Select from '@/components/@IntranetLibrary/Select';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import { approvalOutlineSelectSx } from '@/components/Approval/@components/ApprovalFormTable';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';
import {
  LongServicePledgeBox,
  LongServicePledgeText,
} from '@/components/Approval/@components/LongServiceLeave';
import { SectionHint } from '@/components/Approval/@components/NewCorporateCard';
import { ResignationEmployeeTable } from '@/components/Approval/@components/Resignation';
import { getLongServiceLeaveDraftSourceUrl } from '@/constant/approval';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  buildLongServiceLeaveSubject,
  changeEndsAt,
  changeServiceYears,
  changeStartsAt,
  isFirstAnniversary,
  LONG_SERVICE_PLEDGE_ALERT,
  SERVICE_YEARS_OPTIONS,
  type LongServiceLeaveDraftSource,
  type LongServiceLeaveFormState,
  type LongServicePledge,
} from '@/utils/approval/longServiceLeave';
import { VEHICLE_COST_HR_MISSING_MESSAGE } from '@/utils/approval/vehicleCost';
import { formatAsDate } from '@/utils/format';
import { FieldBlock, FieldHeader } from './styles';
import type { ApprovalFormValues } from './types';

export const LONG_SERVICE_PLEDGE_ANCHOR_ID = 'long-service-pledge';

const HrMissingNotice = styled(ApprovalNoticeBox)`
  color: var(--dk-danger, #f55060);
`;

const InlineRow = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px 12px;
`;

const SubField = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Separator = styled(Box)`
  padding-bottom: 10px;
`;

const UseCount = styled('strong')`
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  font-size: 16px;
  color: var(--dk-primary, #1e6cfa);
`;

const ErrorText = styled('span')`
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  font-size: 14px;
  color: var(--dk-danger, #f55060);
`;

const AlertText = styled('p')`
  margin: 8px 0 0;
  font-size: 12px;
  color: var(--dk-danger, #f55060);
`;

const PledgeAgreeRow = styled(Box)`
  display: flex;
  justify-content: flex-end;
  margin-top: 15px;
`;

const PledgeAlert = styled(AlertText)`
  margin-top: 4px;
  text-align: right;
`;

const toPickerValue = (value: string): Dayjs | null =>
  value ? dayjs(value) : null;

const toDateString = (value: Dayjs | null): string =>
  value?.isValid() ? formatAsDate(value) : '';

/** F&CO 장기근속 포상휴가(240) 본문 — 레거시 appr_edit_forms_hr_longservice_leave */
const LongServiceLeaveField = () => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'longServiceLeave' });
  const first = state ? isFirstAnniversary(state.serviceYears) : false;

  const { data: countSource, error: countError } =
    useSWR<LongServiceLeaveDraftSource>(
      state && !first && state.startsAt && state.endsAt
        ? getLongServiceLeaveDraftSourceUrl(240, {
            startsAt: state.startsAt,
            endsAt: state.endsAt,
          })
        : null,
      getNestedDataFetcher,
      { revalidateOnFocus: false, shouldRetryOnError: false },
    );
  const fetchedCount = countSource?.display?.useCount;

  useEffect(() => {
    const current = getValues('longServiceLeave');
    if (!current || isFirstAnniversary(current.serviceYears)) return;
    const useCount =
      typeof fetchedCount === 'number' && !countError
        ? String(fetchedCount)
        : '';
    if (current.useCount === useCount) return;
    setValue('longServiceLeave', { ...current, useCount });
  }, [
    fetchedCount,
    countError,
    state?.useCount,
    state?.serviceYears,
    getValues,
    setValue,
  ]);

  const update = useCallback(
    (
      next: (current: LongServiceLeaveFormState) => LongServiceLeaveFormState,
    ) => {
      const current = getValues('longServiceLeave');
      if (!current) return;
      setValue('longServiceLeave', next(current), { shouldDirty: true });
    },
    [getValues, setValue],
  );

  if (!state) {
    return (
      <FieldBlock>
        <HrMissingNotice>{t(VEHICLE_COST_HR_MISSING_MESSAGE)}</HrMissingNotice>
      </FieldBlock>
    );
  }

  // 레거시 — 포상 내용을 바꾸면 직접 고친 제목도 덮어씀
  const handleServiceYears = (serviceYears: string) => {
    update((current) => changeServiceYears(current, serviceYears));
    setValue(
      'subject',
      buildLongServiceLeaveSubject(serviceYears, state.applicant),
      { shouldDirty: true },
    );
  };

  const { applicant } = state;

  return (
    <Box>
      <FieldBlock>
        <FieldHeader>
          <InfoSectionTitle>{t('신청자 정보')}</InfoSectionTitle>
        </FieldHeader>
        <ResignationEmployeeTable
          employee={{
            name: applicant.name,
            employeeNumber: applicant.employeeNumber,
            affiliation: applicant.affiliation,
            enterDate: applicant.hireDate,
          }}
        />
      </FieldBlock>

      <FieldBlock>
        <FieldHeader>
          <InfoSectionTitle>{t('포상 내용')}</InfoSectionTitle>
        </FieldHeader>
        <Select
          value={state.serviceYears}
          options={SERVICE_YEARS_OPTIONS.map((years) => ({
            value: years,
            label: t(years),
          }))}
          onChange={(e) => handleServiceYears(e.target.value as string)}
          sx={{ ...approvalOutlineSelectSx, width: 180 }}
        />
        <AlertText>
          {t('* 포상 관련한 일체의 증빙을 첨부파일로 등록 바랍니다.')}
        </AlertText>
      </FieldBlock>

      {!first && (
        <FieldBlock>
          <FieldHeader>
            <InfoSectionTitle>
              {t('장기근속 포상휴가 사용 기간')}
            </InfoSectionTitle>
          </FieldHeader>
          <SectionHint sx={{ mt: 0, mb: 1.5 }}>
            {t('* 사용일수는 워킹데이 기준으로 적용됩니다.')}
          </SectionHint>
          <InlineRow>
            <SubField>
              <Label size={14} isRequired={false} title={t('시작일')} />
              <DatePicker
                value={toPickerValue(state.startsAt)}
                onChange={(value) =>
                  update((current) =>
                    changeStartsAt(current, toDateString(value)),
                  )
                }
                format="YYYY-MM-DD"
                placeholder="yyyy-mm-dd"
                sx={{ width: 160 }}
              />
            </SubField>
            <Separator>~</Separator>
            <SubField>
              <Label size={14} isRequired={false} title={t('종료일')} />
              <DatePicker
                value={toPickerValue(state.endsAt)}
                onChange={(value) =>
                  update((current) =>
                    changeEndsAt(current, toDateString(value)),
                  )
                }
                minDate={toPickerValue(state.startsAt) ?? undefined}
                format="YYYY-MM-DD"
                placeholder="yyyy-mm-dd"
                sx={{ width: 160 }}
              />
            </SubField>
            <Separator>=</Separator>
            <SubField>
              <Label size={14} isRequired={false} title={t('사용일수')} />
              {countError ? (
                <ErrorText>{t('데이터를 불러오지 못했습니다.')}</ErrorText>
              ) : (
                <UseCount>{state.useCount || '-'}</UseCount>
              )}
            </SubField>
          </InlineRow>
        </FieldBlock>
      )}

      <FieldBlock id={LONG_SERVICE_PLEDGE_ANCHOR_ID}>
        <FieldHeader>
          <InfoSectionTitle>
            {t('장기근속 포상휴가 사용 서약')}
          </InfoSectionTitle>
        </FieldHeader>
        <LongServicePledgeBox>
          <LongServicePledgeText />
        </LongServicePledgeBox>
        <PledgeAgreeRow>
          <RadioGroupV2
            row
            value={state.pledgeAgree}
            onChange={(e) => {
              const pledgeAgree = e.target.value as LongServicePledge;
              update((current) => ({
                ...current,
                pledgeAgree,
                pledgeAlert: pledgeAgree !== 'Y' && current.pledgeAlert,
              }));
            }}
            options={[
              { value: 'Y', label: t('동의함') },
              { value: 'N', label: t('동의하지 않음') },
            ]}
          />
        </PledgeAgreeRow>
        {(state.pledgeAgree === 'N' || state.pledgeAlert) && (
          <PledgeAlert>{t(LONG_SERVICE_PLEDGE_ALERT)}</PledgeAlert>
        )}
      </FieldBlock>
    </Box>
  );
};

export default LongServiceLeaveField;
